import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { Seo, SITE_URL } from "@/components/Seo";
import { Footer } from "@/components/Footer";
import { Spinner } from "@/components/Loaders";
import { CaretLeft, House } from "@phosphor-icons/react";

const CAT_LINKS = [
  { label: "Farmhouses in Hyderabad", to: "/hyderabad-farmhouses" },
  { label: "Resorts in Hyderabad", to: "/hyderabad-resorts" },
  { label: "Banquet Halls in Hyderabad", to: "/hyderabad-banquet-halls" },
  { label: "Function Halls in Hyderabad", to: "/hyderabad-function-halls" },
];

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(undefined);
  useEffect(() => { api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setPost(null)); }, [slug]);

  if (post === undefined) return <div className="flex justify-center py-24"><Spinner /></div>;
  if (post === null) return (
    <div className="px-4 py-24 text-center">
      <p className="font-heading text-xl text-stone-800">Article not found</p>
      <Link to="/blog" className="mt-3 inline-block text-primary underline">Back to blog</Link>
    </div>
  );

  const paragraphs = (post.content || "").split(/\n\s*\n/).filter(Boolean);
  return (
    <>
      <Seo title={post.seo_title || post.title} path={`/blog/${post.slug}`} type="article" image={post.featured_image ? fileUrl(post.featured_image) : undefined}
        description={post.meta_description || post.excerpt}
        jsonLd={{ "@context": "https://schema.org", "@type": "Article", headline: post.title,
          image: post.featured_image ? fileUrl(post.featured_image) : undefined, author: { "@type": "Organization", name: post.author || "Avinya Bookings" },
          datePublished: post.published_at, publisher: { "@type": "Organization", name: "Avinya Bookings" },
          mainEntityOfPage: `${SITE_URL}/blog/${post.slug}` }} />

      <header className="border-b border-stone-200 bg-white/90 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center gap-3">
          <button onClick={() => navigate("/blog")} className="text-stone-500 hover:text-stone-800"><CaretLeft size={20} /></button>
          <span className="font-heading text-lg font-bold text-primary">Avinya Blog</span>
        </div>
      </header>

      <article className="mx-auto max-w-3xl px-4 py-8">
        <nav className="text-xs text-stone-400" data-testid="breadcrumb"><Link to="/" className="hover:underline">Home</Link> › <Link to="/blog" className="hover:underline">Blog</Link> › <span className="text-stone-600">{post.category}</span></nav>
        <span className="mt-3 inline-block text-xs font-medium text-primary">{post.category}</span>
        <h1 className="mt-1 font-heading text-3xl font-bold leading-tight text-stone-900 md:text-4xl">{post.title}</h1>
        <p className="mt-2 text-sm text-stone-400">{post.author}{post.published_at ? ` · ${new Date(post.published_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}` : ""}</p>
        {post.featured_image && <img src={fileUrl(post.featured_image)} alt={post.title} className="mt-5 aspect-video w-full rounded-2xl object-cover" />}
        <div className="prose prose-stone mt-6 max-w-none">
          {paragraphs.map((para, i) => <p key={i} className="mb-4 text-base leading-relaxed text-stone-700">{para}</p>)}
        </div>

        <div className="mt-10 rounded-2xl border border-stone-200 bg-accent/30 p-6">
          <p className="flex items-center gap-2 font-heading font-semibold text-stone-900"><House size={18} weight="duotone" className="text-primary" /> Ready to book?</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {CAT_LINKS.map((l) => <Link key={l.to} to={l.to} className="rounded-full border border-primary/30 bg-white px-4 py-1.5 text-sm font-medium text-primary hover:bg-primary hover:text-white">{l.label}</Link>)}
          </div>
        </div>
      </article>
      <Footer />
    </>
  );
}
